<template>
  <div v-if="referralCampaign">
    <PageHeader
      image-url="https://d2ezpplblfgpjx.cloudfront.net/refer-friends-mobile.png">
      <template #description>
        <!-- The <title> tag sets the browser tab title, so we use <h1> for styled page content -->
        <h1 class="text-white text-capitalize">
          Refer A Friend
        </h1>
        <h3>
          <span>You'll get ${{ referrerCredit }}.</span>
          <span> She'll get our best deal: Up to {{ refereeDiscount }}% off her first month of Armoire</span>
          <span v-if="bonusItemsText"> and {{ bonusItemsText }}</span>
          <span>!*</span>
        </h3>
        <BaseLink
          v-if="isMobile"
          class="color-white text-link text-center"
          @click="showDetails = !showDetails">
          {{ showDetails ? 'Hide Details' : 'Show Details' }}
        </BaseLink>
        <div
          v-if="showDetails || !isMobile"
          class="details-section">
          <p>
            <span>Share Armoire with your friends and network! As a thank you, you'll get ${{ referrerCredit }} subscription credit for every friend who signs up using your link or code.</span>
            <span> They'll get <span class="bold">our best deal on their first month of Armoire</span></span>
            <span v-if="bonusItemsText">, plus {{ bonusItemsText }} in their first case</span>
            <span>!</span>
          </p>
          <p class="fine-print text-white mt-sm">
            * Subscription credit and initial discount valid for monthly subscriptions, including our Unlimited, 7-Item Monthly and 4-Item Monthly Plans
          </p>
        </div>
      </template>
    </PageHeader>
    <ReferralShareSection
      v-if="!isMobile"
      :referral-code="referralCode"
      :referral-campaign="referralCampaign"/>
    <ReferralShareSectionMobile
      v-if="isMobile"
      :referral-code="referralCode"
      :referral-campaign="referralCampaign"/>
    <div class="mt-xl bg-origami w-100 p-4">
      <div class="d-flex justify-content-center">
        <h4>Your Referrals + Rewards</h4>
      </div>
      <ReferralRewardSummary
        v-if="referrals && referrals.length"
        class="mt-lg"
        :credit-available="creditAvailable"
        :referrals="referrals"
        :referral-awards="referralAwards"/>
      <div
        v-else
        class="d-flex justify-content-center text-center mt-lg">
        <div class="p-2">
          <h5>You haven't earned any rewards (yet)!</h5>
          <p>
            When a friend signs up using your link, we'll send you a notification and display your reward balance here.
          </p>
        </div>
      </div>
      <div
        v-if="referrals && referrals.length"
        class="my-xxl bg-origami w-100">
        <ReferralHistoryMobile
          v-if="isMobile"
          :referrals="referrals"/>
        <ReferralHistory
          v-else
          :referrals="referrals"/>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import ReferralRewardSummary from './ReferralRewardSummary.vue'
import ReferralHistory from './ReferralHistory.vue'
import ReferralShareSection from './ReferralShareSection.vue'
import ReferralShareSectionMobile from './ReferralShareSectionMobile.vue'
import ReferralHistoryMobile from './ReferralHistoryMobile.vue'
import useAnalytics from '@shared/composables/analytics.js'
import PageHeader from '@shared/components/ADORN/PageHeader.vue'
import useScreenSize from '@shared/composables/screenSize.js'
import { useHideIntercomLauncher } from '@shared/composables/intercom.js'
import BaseLink from '@shared/components/BaseLink.vue'
import { storeToRefs } from 'pinia'
import { useClientStore } from '@shared/stores/client.js'
import { useSubscriptionStore } from '@/stores/subscribe.js'

const { isMobile } = useScreenSize()
const { track } = useAnalytics()

useHideIntercomLauncher(isMobile)

const showDetails = ref(false)

const clientStore = useClientStore()
const { referralCode, referrals, referralAwards, balance } = storeToRefs(clientStore)
const subscribeStore = useSubscriptionStore()
const { referralCampaign, refereeDiscount, refereeBonusItems } = storeToRefs(subscribeStore)

const bonusItemsText = computed(() => {
  return refereeBonusItems.value > 0
    ? `${refereeBonusItems.value} free bonus item${refereeBonusItems.value > 1 ? 's' : ''}`
    : null
})
const referrerCredit = computed(() => {
  const { referrerCredit } = referralCampaign.value ?? {}
  return (referrerCredit ?? 0) / 100
})
const creditAvailable = computed(() => {
  // Stripe returns user credit as a negative amount, so flip the sign
  return balance.value < 0 ? -balance.value : 0
})

const { getReferrals, getBilling } = clientStore
const { getReferralCampaign } = subscribeStore

onMounted(() => {
  getReferrals()
  getReferralCampaign()
  getBilling()
  track('Viewed Refer Friends')
})
</script>
